import * as React from "react";

import {
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, UseFormGetValues } from "react-hook-form";
import { UseFormRegister, FormState, UseFormClearErrors, UseFormSetValue, Control } from "react-hook-form/dist/types";

import { ALLOWED_MAX_DISK_SIZE } from "src/lib/const";

const Row = styled("div")(() => ({
  marginBottom: "30px",
  display: "flex",
  justifyContent: "space-between",
}));

const MenuTitle = styled(Typography)(({ theme }) => ({
  marginLeft: 16,
  marginRight: 16,
  paddingTop: 5,
  paddingBottom: 5,
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
}));

const MenuTitleBackground = styled("div")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 450,
    },
  },
};

export interface TemplateDiskInfo {
  id: string;
  name: string;
  size: number;
  isEditForm: boolean;
}

interface DiskInputProps {
  register: UseFormRegister<any>;
  formState: FormState<any>;
  clearErrors: UseFormClearErrors<any>;
  availableDisksFetchError: Error;
  availableDisks: Disk[];
  inUseDisks: Disk[];
  diskInfo: TemplateDiskInfo;
  disabled: {
    newDiskDisabled: boolean;
    existingDiskDisabled: boolean;
    setNewDiskDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    setExistingDiskDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  };
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  control: Control<any, object>;
  setExistingDiskSize: React.Dispatch<React.SetStateAction<number>>;
}

export const NoExistingDisk = "No Existing Disks Available";

export const DiskInfoInput: React.FC<DiskInputProps> = ({
  register,
  formState,
  clearErrors,
  availableDisksFetchError,
  availableDisks,
  inUseDisks,
  diskInfo,
  disabled,
  setValue,
  getValues,
  control,
  setExistingDiskSize,
}) => {
  const [origExistingDiskSize, setOrigExistingDiskSize] = React.useState(diskInfo.size);
  const { newDiskDisabled, existingDiskDisabled, setNewDiskDisabled, setExistingDiskDisabled } = disabled;
  const [selectedDisk, setSelectedDisk] = React.useState(diskInfo.name);
  const [editSizeAlert, setEditSizeAlert] = React.useState(false);
  const templateDiskUnavailable = React.useMemo((): boolean => {
    // If we aren't using a template with disk information OR we are just editing a template, then availability doesn't matter here.
    if (!diskInfo.name || diskInfo.isEditForm) {
      return false;
    }

    const notAvailable = availableDisks?.findIndex((disk) => disk.name === diskInfo.name) < 0;

    return notAvailable && selectedDisk === diskInfo.name;
  }, [diskInfo, availableDisks, selectedDisk]);

  const existingDisksLabel = React.useMemo(() => {
    if (!availableDisks && !availableDisksFetchError) {
      return "Loading...";
    }
    return "Existing Disk";
  }, [availableDisks, availableDisksFetchError]);

  const getExistingSize = (value: string) => {
    // Value cleared, return early.
    if (!value) {
      return "";
    }

    // Template Disk exists, is selected, and is unavailable OR the existing disk fields are disabled -- then there should not be a value in the existing disk size field.
    if (templateDiskUnavailable || existingDiskDisabled) {
      return "";
    }

    // Template exists, but value has been manually changed.
    if (diskInfo.size && diskInfo.size.toString() !== value) {
      return value;
    }

    // Template exists, it hasn't changed, and disk is available.
    if (diskInfo.name && !templateDiskUnavailable) {
      return diskInfo.size;
    }

    // Fallback
    return value;
  };

  const getError = (): boolean => {
    return !!formState.errors?.existingDiskName || templateDiskUnavailable;
  };

  const clearNew = () => {
    setNewDiskDisabled(true);
    setValue("newDiskName", "");
    setValue("newDiskSize", null);
    clearAllErrors();
  };

  const clearExisting = () => {
    setExistingDiskDisabled(true);
    setSelectedDisk("");
    setExistingDiskSize(null);
    setValue("existingDiskName", "");
    setValue("existingDiskSize", null);
    clearAllErrors();
  };

  const clearAllErrors = () => {
    clearErrors("newDiskName");
    clearErrors("newDiskSize");
    clearErrors("existingDiskName");
    clearErrors("existingDiskSize");
  };

  return (
    <>
      {!diskInfo.isEditForm && (
        <Row>
          <TextField
            required={availableDisks?.length === 0}
            id="new-disk"
            inputProps={{
              "data-testid": "new-disk",
            }}
            label="New Disk"
            sx={{ width: "58%" }}
            helperText={formState.errors?.newDiskName?.message || "Pick a name for disk"}
            defaultValue=""
            {...register("newDiskName", {
              disabled: newDiskDisabled,
            })}
            error={!!formState.errors?.newDiskName}
            onChange={(e) => {
              if (!e.target.value) {
                if (!getValues("newDiskSize")) {
                  setExistingDiskDisabled(false);
                }
              } else {
                clearExisting();
              }
            }}
          />
          <TextField
            required={availableDisks?.length === 0}
            disabled={newDiskDisabled}
            id="new-disk-size"
            inputProps={{
              "data-testid": "new-disk-size",
              min: 5,
              max: ALLOWED_MAX_DISK_SIZE,
            }}
            label="Size (GB)"
            type="number"
            helperText={formState.errors?.newDiskSize?.message || `Between 5 to ${ALLOWED_MAX_DISK_SIZE} GB`}
            sx={{ width: "40%" }}
            {...register("newDiskSize", {
              disabled: newDiskDisabled,
            })}
            error={!!formState.errors?.newDiskSize}
            onChange={(e) => {
              if (!e.target.value) {
                if (!getValues("newDiskName")) {
                  setExistingDiskDisabled(false);
                }
              } else {
                clearExisting();
              }
            }}
          />
        </Row>
      )}
      <Row>
        <FormControl
          sx={{
            width: "58%",
          }}
          error={getError()}
        >
          <InputLabel id="existing-disk-label">{existingDisksLabel}</InputLabel>
          <Select
            id="existing-disk"
            inputProps={{
              "data-testid": "existing-disk",
              inputRef: (ref) => {
                if (!ref) return;
                register("existingDiskName");
              },
            }}
            label={existingDisksLabel}
            labelId="existing-disk-label"
            value={selectedDisk}
            disabled={existingDiskDisabled || !!availableDisksFetchError}
            onChange={(e) => {
              setSelectedDisk(e.target.value);
              setValue("existingDiskName", e.target.value);
              if (!e.target.value) {
                setNewDiskDisabled(false);
                setExistingDiskSize(null);
              } else {
                clearNew();
              }
            }}
            MenuProps={MenuProps}
          >
            <MenuItem key="empty" value="" onClick={() => setValue("existingDiskSize", undefined)}>
              <em>None</em>
            </MenuItem>
            {diskInfo.isEditForm && availableDisks?.length > 0 && (
              <MenuTitleBackground>
                <MenuTitle>AVAILABLE DISKS</MenuTitle>
              </MenuTitleBackground>
            )}
            {availableDisks?.length > 0 &&
              availableDisks.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.name}
                  onClick={() => {
                    setOrigExistingDiskSize(option.size);
                    setExistingDiskSize(Number(option.size));
                    setValue("existingDiskSize", option.size);
                  }}
                >
                  {option.name}
                </MenuItem>
              ))}
            {diskInfo.isEditForm && inUseDisks?.length > 0 && (
              <MenuTitleBackground>
                <MenuTitle>IN-USE DISKS</MenuTitle>
              </MenuTitleBackground>
            )}
            {inUseDisks?.length > 0 &&
              inUseDisks.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.name}
                  onClick={() => {
                    setOrigExistingDiskSize(option.size);
                    setExistingDiskSize(Number(option.size));
                    setValue("existingDiskSize", option.size);
                  }}
                >
                  {option.name}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>
            {templateDiskUnavailable
              ? "Disk not available, please select another Disk"
              : availableDisks?.length === 0
              ? NoExistingDisk
              : "Select existing disk"}
          </FormHelperText>
        </FormControl>
        <Controller
          control={control}
          name="existingDiskSize"
          render={({ field: { onChange, value } }) => (
            <TextField
              id="update-disk-size"
              disabled={existingDiskDisabled || !!availableDisksFetchError}
              inputProps={{
                "data-testid": "update-disk-size",
                min: origExistingDiskSize,
                max: ALLOWED_MAX_DISK_SIZE,
              }}
              value={getExistingSize(value)}
              label="Size (GB)"
              type="number"
              helperText={
                formState.errors?.existingDiskSize?.message ||
                (origExistingDiskSize && `Between ${origExistingDiskSize} to ${ALLOWED_MAX_DISK_SIZE} GB`)
              }
              sx={{ width: "40%" }}
              error={!!formState.errors?.existingDiskSize || templateDiskUnavailable}
              onChange={(e) => {
                onChange(e);
                if (!e.target.value) {
                  setEditSizeAlert(false);
                  if (!getValues("existingDiskName")) {
                    setNewDiskDisabled(false);
                  }
                } else {
                  clearNew();
                  setEditSizeAlert(true);
                }
              }}
            />
          )}
        />
      </Row>
      {!!availableDisksFetchError && (
        <Alert severity="warning" sx={{ marginBottom: "15px" }}>
          There was a problem while looking up your existing disks. Please refresh the page or create a workflow using a
          new disk. If the problem persists, please contact support.
        </Alert>
      )}
      {editSizeAlert && (
        <Alert severity="warning" sx={{ marginBottom: "15px" }}>
          Disk size may only be increased once every 6 hours
        </Alert>
      )}
    </>
  );
};
